// Mapbox-backed polygon view for the OT's parcels. Reads the token
// from `VITE_MAPBOX_TOKEN`; renders a placeholder card when the token
// is missing so the rest of the page still works.
//
// Each parcel is rendered as a filled polygon coloured by compliance
// state. Selecting a parcel (in the table or via a click) flies the
// camera to its centroid and highlights its outline.

import { useEffect, useMemo, useRef } from "react"
import * as turf from "@turf/turf"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { useTranslation } from "react-i18next"
import { MapPinOff } from "lucide-react"

import { env } from "@/config/env"
import { Card } from "@/components/ui/card"
import type {
  ParcelGeoEntry,
  ParcelLocationPoint,
} from "@/features/transit/api/types"

interface Props {
  parcels: ParcelGeoEntry[]
  selectedId?: number | null
  onSelect?: (parcel: ParcelGeoEntry) => void
  // Optional overlay slot rendered inside the map's relative-positioned
  // Card. Used by the parcels tab to inject the navigation strip.
  children?: React.ReactNode
}

const COLOR_OK = "#8b5cf6" // violet-500 — brand-aligned base for compliant parcels
const COLOR_RISK = "#ef4444" // red-500 — at-risk stays red for clear signal
const COLOR_SELECTED = "#f59e0b" // amber-500 — readable highlight on light tiles
const SOURCE_ID = "ot-parcels"
const FILL_LAYER_ID = "ot-parcels-fill"
const OUTLINE_LAYER_ID = "ot-parcels-outline"
const SELECTED_LAYER_ID = "ot-parcels-selected"

export function TraceabilityParcelsMap({
  parcels,
  selectedId,
  onSelect,
  children,
}: Props) {
  const { t } = useTranslation("transit")
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const styleLoadedRef = useRef(false)

  const featureCollection = useMemo(
    () => buildFeatureCollection(parcels),
    [parcels],
  )

  // Initialise the map once. Container ref is stable; `parcels` updates
  // are handled via the data-update effect below.
  useEffect(() => {
    if (!env.mapboxToken) return
    if (!containerRef.current) return
    if (mapRef.current) return

    mapboxgl.accessToken = env.mapboxToken
    const map = new mapboxgl.Map({
      container: containerRef.current,
      // `light-v11` is Mapbox's minimal monochrome style — calmer than
      // satellite for an EUDR overview and keeps polygon colours
      // (compliant green / risk red) from competing with imagery.
      style: "mapbox://styles/mapbox/light-v11",
      // Sensible default for Côte d'Ivoire cocoa belt; flyTo replaces it
      // once we know the parcel bounds.
      center: [-5.5471, 7.539],
      zoom: 6,
    })
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }))
    map.addControl(new mapboxgl.FullscreenControl())

    map.on("load", () => {
      styleLoadedRef.current = true
      installSourceAndLayers(map)
      pushData(map, featureCollection)
      // Initial framing: fly to the first parcel rather than the full
      // bbox. Operators usually want to start with the first plot in
      // focus, then step through with the navigation strip.
      const first = parcels.find(
        (p) => polygonRing(p.locations) !== null,
      )
      const firstCenter = parcelCentroid(first)
      if (firstCenter) {
        map.jumpTo({ center: firstCenter, zoom: 16 })
      } else {
        fitBoundsToFeatures(map, featureCollection)
      }

      map.on("click", FILL_LAYER_ID, (event) => {
        const feature = event.features?.[0]
        if (!feature) return
        const id = feature.properties?.id
        if (typeof id !== "number" || !onSelect) return
        const parcel = parcels.find((p) => p.id === id)
        if (parcel) onSelect(parcel)
      })
      map.on("mouseenter", FILL_LAYER_ID, () => {
        map.getCanvas().style.cursor = "pointer"
      })
      map.on("mouseleave", FILL_LAYER_ID, () => {
        map.getCanvas().style.cursor = ""
      })
    })

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      styleLoadedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Push fresh data when the parcel set changes. Refit only when no
  // parcel is selected — otherwise the selection effect's flyTo wins.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleLoadedRef.current) return
    pushData(map, featureCollection)
    if (selectedId == null) {
      fitBoundsToFeatures(map, featureCollection)
    }
  }, [featureCollection, selectedId])

  // Highlight the selected polygon by updating the filter on the
  // dedicated layer. Cheaper than re-pushing the source.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleLoadedRef.current) return
    const filter: mapboxgl.FilterSpecification =
      selectedId == null
        ? ["==", "id", -1]
        : ["==", "id", selectedId]
    map.setFilter(SELECTED_LAYER_ID, filter)
    if (selectedId != null) {
      const target = parcels.find((p) => p.id === selectedId)
      const center = parcelCentroid(target)
      if (center) {
        map.flyTo({ center, zoom: 16, duration: 1200 })
      }
    }
  }, [selectedId, parcels])

  if (!env.mapboxToken) {
    return (
      <Card className="flex flex-col items-center justify-center gap-2 border-dashed bg-muted/20 p-8 text-center">
        <MapPinOff className="size-6 text-muted-foreground" />
        <div className="text-sm font-medium text-foreground">
          {t("trace.map.tokenMissingTitle")}
        </div>
        <p className="max-w-sm text-xs text-muted-foreground">
          {t("trace.map.tokenMissingHint")}
        </p>
      </Card>
    )
  }

  if (featureCollection.features.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center gap-2 border-dashed bg-muted/20 p-8 text-center">
        <MapPinOff className="size-6 text-muted-foreground" />
        <div className="text-sm font-medium text-foreground">
          {t("trace.map.noGeometryTitle")}
        </div>
        <p className="max-w-sm text-xs text-muted-foreground">
          {t("trace.map.noGeometryHint")}
        </p>
      </Card>
    )
  }

  return (
    <Card className="relative overflow-hidden p-0">
      <div ref={containerRef} className="h-[420px] w-full" />
      {children}
    </Card>
  )
}

// ── helpers ────────────────────────────────────────────────────

function installSourceAndLayers(map: mapboxgl.Map) {
  if (map.getSource(SOURCE_ID)) return

  map.addSource(SOURCE_ID, {
    type: "geojson",
    data: emptyCollection(),
  })

  map.addLayer({
    id: FILL_LAYER_ID,
    type: "fill",
    source: SOURCE_ID,
    paint: {
      "fill-color": [
        "case",
        ["get", "atRisk"],
        COLOR_RISK,
        COLOR_OK,
      ],
      "fill-opacity": 0.35,
    },
  })

  map.addLayer({
    id: OUTLINE_LAYER_ID,
    type: "line",
    source: SOURCE_ID,
    paint: {
      "line-color": [
        "case",
        ["get", "atRisk"],
        COLOR_RISK,
        COLOR_OK,
      ],
      "line-width": 1.5,
    },
  })

  map.addLayer({
    id: SELECTED_LAYER_ID,
    type: "line",
    source: SOURCE_ID,
    paint: {
      "line-color": COLOR_SELECTED,
      "line-width": 3,
    },
    filter: ["==", "id", -1] as mapboxgl.FilterSpecification,
  })
}

function pushData(map: mapboxgl.Map, fc: GeoJSON.FeatureCollection) {
  const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
  if (source) source.setData(fc)
}

function fitBoundsToFeatures(map: mapboxgl.Map, fc: GeoJSON.FeatureCollection) {
  if (fc.features.length === 0) return
  try {
    const bbox = turf.bbox(fc)
    map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      { padding: 40, duration: 0, maxZoom: 14 },
    )
  } catch {
    // turf can throw on degenerate geometries — leave the camera alone.
  }
}

function emptyCollection(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: [] }
}

function buildFeatureCollection(parcels: ParcelGeoEntry[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  for (const parcel of parcels) {
    const ring = polygonRing(parcel.locations)
    if (!ring) continue
    features.push({
      type: "Feature",
      // Mapbox's promoteId would be cleaner but typed properties keep
      // the rest of the layer styling simple.
      id: parcel.id,
      properties: {
        id: parcel.id,
        atRisk:
          parcel.isAtRiskDeforested === true || parcel.status === "AT_RISK",
        parcelId: parcel.parcelId ?? null,
        hectares: parcel.hectares,
      },
      geometry: { type: "Polygon", coordinates: [ring] },
    })
  }
  return { type: "FeatureCollection", features }
}

function polygonRing(points: ParcelLocationPoint[] | null): number[][] | null {
  if (!points || points.length < 3) return null
  const ring = points.map((p) => [p.longitude, p.latitude])
  // GeoJSON polygons must be closed (first === last).
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first)
  return ring
}

function parcelCentroid(parcel: ParcelGeoEntry | undefined): [number, number] | null {
  if (!parcel) return null
  // Prefer the stored centroid string ("lng,lat"). Fall back to
  // turf-computed centroid of the polygon.
  if (parcel.centroid) {
    const parts = parcel.centroid.split(",").map(Number)
    if (parts.length === 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
      return [parts[0], parts[1]]
    }
  }
  const ring = polygonRing(parcel.locations)
  if (!ring) return null
  try {
    const c = turf.centroid({ type: "Polygon", coordinates: [ring] })
    return c.geometry.coordinates as [number, number]
  } catch {
    return null
  }
}
